import { truncateChartLabel } from './trade-study-chart-options';

export type TimeRow = {
  process_name: string;
  total_time: number;
};

export type CostRow = {
  process_name: string;
  total_cost: number;
};

export type CompositionRow = {
  process_name: string;
  subtotal_material_cost: number;
  subtotal_labour_cost: number;
  subtotal_capex: number;
};

export type TimeDistributionRow = {
  process_name: string;
  total_time: number;
};

export type ShiftRow = {
  shift_label: string;
  required_hours_per_month: number;
  capacity_hours_per_month: number;
};

export type CompletionRow = {
  shift_label: string;
  months: number;
};

export function buildTimeBarChartData(
  rows: TimeRow[],
  colors: { time: string; timeBorder: string },
) {
  return {
    labels: rows.map((r) => truncateChartLabel(r.process_name)),
    datasets: [
      {
        label: 'Total Time (hours)',
        data: rows.map((r) => r.total_time),
        backgroundColor: colors.time,
        borderColor: colors.timeBorder,
        borderWidth: 0,
      },
    ],
  };
}

export function buildCostBarChartData(
  rows: CostRow[],
  currencyCode: string,
  colors: { cost: string; costBorder: string },
) {
  return {
    labels: rows.map((r) => truncateChartLabel(r.process_name)),
    datasets: [
      {
        label: `Total Cost (${currencyCode})`,
        data: rows.map((r) => r.total_cost),
        backgroundColor: colors.cost,
        borderColor: colors.costBorder,
        borderWidth: 0,
      },
    ],
  };
}

export function buildCostCompositionChartData(
  rows: CompositionRow[],
  colors: {
    materialCost: string;
    labourCost: string;
    capex: string;
  },
) {
  return {
    labels: rows.map((r) => truncateChartLabel(r.process_name)),
    datasets: [
      {
        label: 'Material Cost',
        data: rows.map((r) => r.subtotal_material_cost),
        backgroundColor: colors.materialCost,
      },
      {
        label: 'Labour Cost',
        data: rows.map((r) => r.subtotal_labour_cost),
        backgroundColor: colors.labourCost,
      },
      {
        label: 'CapEx',
        data: rows.map((r) => r.subtotal_capex),
        backgroundColor: colors.capex,
      },
    ],
  };
}

export function buildPieChartData(
  rows: TimeDistributionRow[],
  colors: { piePalette: string[]; pieBorder: string },
) {
  return {
    labels: rows.map((r) => truncateChartLabel(r.process_name, 36)),
    datasets: [
      {
        data: rows.map((r) => r.total_time),
        backgroundColor: colors.piePalette,
        borderWidth: 2,
        borderColor: colors.pieBorder,
      },
    ],
  };
}

export function buildCapacityVsRequiredChartData(
  rows: ShiftRow[],
  colors: {
    required: string;
    requiredBorder: string;
    capacity: string;
    timeBorder: string;
  },
) {
  return {
    labels: rows.map((r) => r.shift_label),
    datasets: [
      {
        label: 'Required (hr / mo)',
        data: rows.map((r) => r.required_hours_per_month),
        backgroundColor: colors.required,
        borderColor: colors.requiredBorder,
        borderWidth: 1,
      },
      {
        label: 'Capacity (hr / mo)',
        data: rows.map((r) => r.capacity_hours_per_month),
        backgroundColor: colors.capacity,
        borderColor: colors.timeBorder,
        borderWidth: 1,
      },
    ],
  };
}

export function buildCompletionMonthsChartData(
  rows: CompletionRow[],
  colors: { completion: string; completionBorder: string },
) {
  return {
    labels: rows.map((r) => r.shift_label),
    datasets: [
      {
        label: 'Months to complete',
        data: rows.map((r) => r.months),
        backgroundColor: colors.completion,
        borderColor: colors.completionBorder,
        borderWidth: 1,
      },
    ],
  };
}
